import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EigentumanteilMasterIdMeta,
  EigentumanteilVersionIdMeta,
  EigentumPersonSichtListEntryMeta,
  GbpfandrechtService,
  GlaeubigerSchuldnerListEntryMeta,
  GlaeubigerSchuldnerResponseMeta,
  GrundpfandrechtMasterIdMeta,
  PersonStammMasterId,
  PfandrechtBelastungListEntryMeta,
  PfandrechtVersionDetailEntryMeta,
  RechteIdMeta,
  SchuldnerMasterId,
  SchuldnerVersionDetailEntryMeta
} from '../../../../core/gb-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { LinkComponent, NavTarget } from '../../../../shared/components/link/link.component';
import { ComparatorService } from '../../../../shared/services/comparator-service';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { Observable, Subscription } from 'rxjs';
import { MapResponse } from '../../../../core/maps-api';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../../shared/components/list-loader/pagination-lookup-result';
import { ListLoaderPaginationBuilder } from '../../../../shared/components/list-loader/list-loader-pagination';
import { AbstractComponent } from '../../../../shared/components/abstract-component/abstract.component';
import { ListLoader } from '../../../../shared/components/list-loader/list-loader';
import { ListLoaderWrapperComponent } from '../../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { KompaktPersongbComponent } from '../../../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { HistoryDirective } from '../../../../shared/directives/history.directive';
import { KompaktGrundpfandrechtComponent } from '../../../../shared/kompaktdarstellung/grundpfandrecht/kompakt-grundpfandrecht.component';
import { KompaktGrundstueckComponent } from '../../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { CapSpinnerSmallComponent } from '../../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { FormatRechteIdPipe } from '../../../../shared/pipes/format/format-rechte-id.pipe';
import { TranslateNamePipe } from '../../../../shared/pipes/simple/translate-name.pipe';
import { PersonStammMasterDetailEntryMeta } from '../../../../core/personstamm-api';

@Component({
  selector: 'cap-personstamm-schuldner',
  templateUrl: './personstamm-schuldner.component.html',
  styleUrls: ['./personstamm-schuldner.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    TranslatePipe,
    KompaktPersongbComponent,
    HistoryDirective,
    LinkComponent,
    KompaktGrundpfandrechtComponent,
    KompaktGrundstueckComponent,
    CapSpinnerSmallComponent,
    FormatRechteIdPipe,
    TranslateNamePipe
  ]
})
export class PersonstammSchuldnerComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  personStammMasterId: PersonStammMasterId;
  @Input() auskunftsperre?: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum | undefined;

  @Input()
  mitHist = true;

  response: GlaeubigerSchuldnerResponseMeta;
  viewModelSchuldner: Array<ViewModelRowSchuldner>;
  pagedViewModelSchuldner: Array<ViewModelRowSchuldner>;

  listLoader: ListLoader<ViewModelRowSchuldner>;
  preloadedIds: Array<SchuldnerMasterId>;
  navTargets = NavTarget;
  private readonly PAGE_SIZE = 30;
  private readonly CURRENT_PAGE = 1;
  private subscription: Subscription = new Subscription();

  constructor(
    private appConfigService: AppConfigService,
    protected appformatConfigService: FormatConfigService,
    public translate: TranslateService,
    private pfandrechtService: GbpfandrechtService,
    private comparatorService: ComparatorService,
    private timepointService: TimepointService
  ) {
    super();
    keysToTranslate<string>([]);
  }

  ngOnInit(): void {
    this.viewModelSchuldner = [];
    this.subscription.add(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        this.pfandrechtService
          .loadPfandrechtIdsSichtPersonstamm(
            this.personStammMasterId.id,
            'SCHULDNER',
            this.timepointService.timepointCtx.timepointIso,
            null,
            this.appConfigService.einstellungHistorischeDaten
          )
          .subscribe((data) => {
            this.preloadedIds = data.schuldnerMasterIds;
            this.loadSchuldner();
          });
      })
    );
  }

  getDetailNavigation(): NavTarget {
    return NavTarget.NAV_GRUNDSTUECK_PFANDRECHTE;
  }

  getQueryParams(vm: ViewModelRowSchuldner) {
    return {
      id: vm.masterId.id,
      versionId: vm.pfandrechtVersion.id.id,
      histEnum: vm.pfandrechtVersion.histInfo.histEnum
    };
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private loadSchuldner() {
    const observable = (offset, limit, sortColumn) => this.createObservable(offset, limit);

    this.listLoader = ListLoaderPaginationBuilder.builder<ViewModelRowSchuldner>()
      .setNoSortModel(true)
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(this.listLoader);
  }

  private createObservable(offset: number, limit: number): Observable<any> {
    const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
    const batchedIds = this.preloadedIds.slice(offset, offset + limit).map((id) => id.id);

    return this.pfandrechtService
      .loadPfandrechtSichtPersonstamm(
        this.personStammMasterId.id,
        [],
        batchedIds,
        'SCHULDNER',
        this.timepointService.timepointCtx.timepointIso,
        null,
        historisierung
      )
      .pipe(
        map((data) => {
          this.viewModelSchuldner = this.toViewModel(data.results);
          return <PaginationLookupResult<EigentumPersonSichtListEntryMeta>>{
            result: this.viewModelSchuldner,
            totalItems: this.preloadedIds.length
          };
        })
      );
  }

  private toViewModel(data: Array<GlaeubigerSchuldnerListEntryMeta>) {
    return data
      .map((d) => new ViewModelRowSchuldner(d, this.comparatorService))
      .sort((a, b) => {
        return this.comparatorService.rechtIdVersionComparator(
          a.rechteId,
          b.rechteId,
          a.pfandrechtVersion.histInfo,
          b.pfandrechtVersion.histInfo,
          a.pfandrechtVersion.histInfo.erstellzeitpunkt,
          b.pfandrechtVersion.histInfo.erstellzeitpunkt
        );
      });
  }
}

class ViewModelRowSchuldner {
  masterId: GrundpfandrechtMasterIdMeta;
  schuldner: Array<SchuldnerVersionDetailEntryMeta>;
  rechteId: RechteIdMeta;
  id: EigentumanteilMasterIdMeta;
  versionId: EigentumanteilVersionIdMeta;
  rechtArt: GlaeubigerSchuldnerListEntryMeta.RechtArtEnum;
  pfandrechtVersion: PfandrechtVersionDetailEntryMeta;
  belastungen: Array<ViewModelRowPfandrechtBelastung>;

  constructor(
    entry: GlaeubigerSchuldnerListEntryMeta,
    private comparatorService: ComparatorService
  ) {
    this.masterId = entry.id;
    this.schuldner = entry.schuldner.sort((a, b) =>
      this.comparatorService.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt)
    );
    this.rechteId = entry.rechteId;
    this.rechtArt = entry.rechtArt;
    this.pfandrechtVersion = entry.version;
    this.belastungen = [];
    entry.belastungen.forEach((b) => this.belastungen.push(new ViewModelRowPfandrechtBelastung(b)));
    this.belastungen.sort((a, b) => this.comparatorService.grundstueckComparator(a.belastung.grundstueck, b.belastung.grundstueck));
  }
}

class ViewModelRowPfandrechtBelastung {
  belastung: PfandrechtBelastungListEntryMeta;
  mapLinkItem: MapResponse;

  constructor(belastung: PfandrechtBelastungListEntryMeta) {
    this.belastung = belastung;
    this.mapLinkItem = <MapResponse>{ url: null };
  }
}
